import {
  MuxSetting,
  MuxGoTo,
  MuxFavorite,
  MuxTabFilter,
  MuxPencil,
  MuxPencilRename,
  MuxPlusCircle,
  MuxTrash,
  MuxCopy,
  MuxExport,
} from "@/Icons";
import { FavoriteItem } from "@/types";
import { IPanelScope, ITreeItem } from "@/interfaces";
import {
  useFavoritesContext,
  ComponentsContext,
  IComponentsContext,
  FiltersContext,
} from "@/contexts";
import { FilterCriteriaPanel } from "../../../FiltersPanel";
import { useRef, useState, useContext, useEffect } from "react";
import { findNodeById } from "@/utils/common";
import styles from "./FavoriteContextMenu.module.scss";
import { FILTERS_TAB_KEY } from "@/constants";
import { CacheService, FiltersService, TreeService } from "@/services";
import { FilterTypes, LocalStorageKeys } from "@/enums";
import { getTranslation, redirect, hasPermission } from "@/utils";
import { truncate } from "@/utils/stringUtils";
import { ConfirmationModal } from "@/shared-components";
import { DeletionPreventionModal } from "@/shared-components/DeletionPreventionModal";
import { useDeletionPrevention } from "../../../FiltersPanel/components/FilterContextMenu/useDeletionPrevention";

export interface props {
  favorite: FavoriteItem;
  scope: IPanelScope;
  items: ITreeItem[];
  setIsEditable: () => void;
  handleFavoriteClick: (f: FavoriteItem) => Promise<void>;
}

export const FavoriteContextMenu = ({
  favorite,
  items,
  scope,
  setIsEditable,
  handleFavoriteClick,
}: props) => {
  const { toggleFavorite, setActiveFavorite, reloadFavorites } =
    useFavoritesContext();
  const [showFilterCriteria, setShowFilterCriteria] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const criteriaOverlayTarget = useRef(null);
  const { DropdownMenu, DropdownMenuBody, DropdownMenuItem } =
    useContext<IComponentsContext>(ComponentsContext);

  const filtersContext = useContext(FiltersContext);
  const isFiltersTab = scope.tab === FILTERS_TAB_KEY;
  const isAutoFilterChild = String(favorite.id).includes("/");

  const currentFilter = findNodeById(items!, favorite.id);
  const [fetchedFilter, setFetchedFilter] = useState<ITreeItem | null>(null);
  const resolvedFilter = currentFilter ?? fetchedFilter;

  useEffect(() => {
    if (currentFilter || !isFiltersTab) return;
    (async () => {
      try {
        const filter = await FiltersService.fetchFilterFromServer(
          scope,
          String(favorite.id).split("/")[0],
          String(favorite.id),
        );
        setFetchedFilter(
          String(favorite.id).indexOf("/") > -1
            ? findNodeById(filter.children, favorite.id) ?? filter
            : filter,
        );
      } catch (e) {
        console.error("FavoriteContextMenu->fetchFilter:", e);
      }
    })();
  }, [favorite.id]);

  const filterType = resolvedFilter
    ? FiltersService.getFilterType(resolvedFilter.serverObject)
    : null;

  const hasEditPermissions =
    hasPermission("custom_views_all") ||
    filtersContext?.settings?.moduleEditPermissions?.some((p) =>
      hasPermission(p),
    );

  // When filter isn't loaded in tree yet, fall back to hasEditPermissions only
  const canEdit =
    (hasEditPermissions && resolvedFilter?.serverObject?.is_public) ||
    (resolvedFilter != null &&
      resolvedFilter?.serverObject?.owner_id == scope.userId);

  const {
    showDeletionPreventionModal,
    deletionPreventionData,
    checkDeletionPrevention,
    closeDeletionPreventionModal,
  } = useDeletionPrevention(String(favorite.id));

  const goToFilterLocation = async () => {
    CacheService.setItemToPersistCache(
      scope,
      LocalStorageKeys.FILTERS_TREE_COLLAPSE_STATE_KEY,
      null,
    );
    setActiveFavorite(null);
    mixpanel.track("favorites", "go-to-filter-location");
    await handleFavoriteClick(favorite);
    setTimeout(TreeService.scrollToSelected, 1000);
  };

  const handleShowCriteriaModal = (e: any) => {
    mixpanel.track("favorites", { Action: "show-filter-criteria" });
    e.stopPropagation = true;
    e.keepOpen = true;
    setShowFilterCriteria(true);
  };

  const handleClickOnDelete = () => {
    setShowDeleteModal(true);
  };

  const handleFilterDeletion = async () => {
    try {
      setShowDeleteModal(false);
      const canDelete = await checkDeletionPrevention();
      if (!canDelete) return;

      if (filtersContext) {
        await filtersContext.deleteFilter(String(favorite.id));
        if (currentFilter) {
          TreeService.deleteTreeItemFromState(items, currentFilter);
          filtersContext.reloadItems(items);
        } else {
          filtersContext.fetchFilters();
        }
      }
      await reloadFavorites();
    } catch (e) {
      console.error(`handleFilterDeletion->`, e);
    }
  };

  return (
    <DropdownMenu
      // Workaround for stupid hover logic
      overlayProps={{
        offset: -10,
        placement: "bottom-end",
        flip: true,
        // Cap height to the available space so the menu scrolls instead of being cut off
        "max-size?": true,
      }}
      toggleElement={({ toggle }: { toggle: () => void }) => {
        return (
          <span onClick={toggle} onKeyDown={toggle} role="button" tabIndex={0}>
            <MuxSetting />
          </span>
        );
      }}
    >
      <DropdownMenuBody>
        <DropdownMenuItem
          onClick={goToFilterLocation}
          text={getTranslation(
            `filters-tree.context-menu.view-in-section.${scope.tab}`,
            `View in All ${scope.tab === FILTERS_TAB_KEY ? "Filters" : "Folders"} Section`,
          )}
          icon={<MuxGoTo />}
        />

        {isFiltersTab && !isAutoFilterChild && (
          <DropdownMenuItem
            disabled={!canEdit}
            onClick={() => {
              mixpanel.track("filters-tree", { Action: "edit-filter" });
              if (filtersContext?.editView) {
                filtersContext.editView(String(favorite.id));
              } else {
                redirect(
                  `/projects/${scope.projectId}/custom_views/${favorite.id}/edit`,
                );
              }
            }}
            icon={<MuxPencil />}
            text={getTranslation("generic.button.edit", "Edit")}
          />
        )}

        {isFiltersTab && !isAutoFilterChild && (
          <DropdownMenuItem
            disabled={!canEdit}
            onClick={() => {
              mixpanel.track("filters-tree", { Action: "rename-filter" });
              setIsEditable();
            }}
            icon={<MuxPencilRename />}
            text={getTranslation("filters-tree.context-menu.rename", "Rename")}
          />
        )}

        {isFiltersTab &&
          !isAutoFilterChild &&
          resolvedFilter &&
          filterType === FilterTypes.USER && (
            <DropdownMenuItem
              disabled={!canEdit}
              onClick={() => {
                mixpanel.track("filters-tree", { Action: "add-sub-filter" });
                filtersContext?.addSubFilter(String(favorite.id));
              }}
              icon={<MuxPlusCircle />}
              text={getTranslation(
                "filters-tree.context-menu.add-sub-filter",
                "Add Sub Filter",
              )}
            />
          )}

        {isFiltersTab && !isAutoFilterChild && (
          <DropdownMenuItem
            disabled={!canEdit}
            onClick={handleClickOnDelete}
            icon={<MuxTrash />}
            text={getTranslation("generic.button.delete", "Delete")}
          />
        )}

        {isFiltersTab && !isAutoFilterChild && resolvedFilter && (
          <DropdownMenuItem
            onClick={handleShowCriteriaModal}
            icon={<MuxTabFilter />}
            text={
              <span
                className={styles.itemText}
                ref={criteriaOverlayTarget}
                role="button"
                tabIndex={0} // Make it focusable
                onKeyDown={() => {}}
              >
                {getTranslation(
                  "filters-tree.context-menu.filter-criteria",
                  "Show Filter Criteria",
                )}
              </span>
            }
          >
            <FilterCriteriaPanel
              data={resolvedFilter}
              filterId={favorite.id}
              target={criteriaOverlayTarget.current}
              show={showFilterCriteria}
              onHide={() => setShowFilterCriteria(false)}
            />
          </DropdownMenuItem>
        )}

        {isFiltersTab && !isAutoFilterChild && resolvedFilter && (
          <DropdownMenuItem
            onClick={() => {
              mixpanel.track("filters-tree", { Action: "export-filter" });
              const fields = resolvedFilter.serverObject?.definition || [];
              const params = {
                export_entity: scope.module,
                export_cv: String(favorite.id),
                export_cv_name: truncate(favorite.name, 40),
                export_fields: fields.join(","),
              };
              redirect(
                `/p/${scope.projectId}/reports?${new URLSearchParams(params).toString()}`,
              );
            }}
            icon={<MuxExport />}
            text={getTranslation(
              "filters-tree.context-menu.export-xlsx",
              "Export to XLSX",
            )}
          />
        )}

        {filtersContext?.copyAsPtql &&
          filterType !== FilterTypes.AUTO_FILTER_ROOT && (
            <DropdownMenuItem
              onClick={() =>
                filtersContext.copyAsPtql!(
                  String(favorite.id),
                  resolvedFilter?.serverObject?.auto_filter ?? undefined,
                )
              }
              icon={<MuxCopy />}
              text={getTranslation(
                "filters-tree.context-menu.copy-as-ptql",
                "Copy as PTQL",
              )}
            />
          )}

        {!favorite.parentId && (
          <DropdownMenuItem
            onClick={() => toggleFavorite(favorite)}
            icon={<MuxFavorite />}
            text={getTranslation(
              "filters-tree.context-menu.remove-from-favorites",
              "Remove From Favorites",
            )}
          />
        )}

        <ConfirmationModal
          show={showDeleteModal}
          title={getTranslation(
            "filters-tree.modal.delete-filter.title",
            "Delete Filter?",
          )}
          body={getTranslation(
            "filters-tree.modal.delete-filter.body",
            "The filter and its sub-filters will be deleted, but the data within will not be lost.",
          )}
          onYes={handleFilterDeletion}
          onNo={() => setShowDeleteModal(false)}
          yesLabel={getTranslation("generic.button.delete", "Delete")}
          noLabel={getTranslation("generic.button.cancel", "Cancel")}
        />

        <DeletionPreventionModal
          show={showDeletionPreventionModal}
          onHide={closeDeletionPreventionModal}
          title={getTranslation(
            "filters-tree.modal.deletion-prevention.title",
            "Failed to Delete View",
          )}
          usedInLabel={getTranslation(
            "filters-tree.modal.deletion-prevention.used-in",
            "Please review the following items and unlink the view before continuing:",
          )}
          deletionPreventionData={deletionPreventionData}
        />
      </DropdownMenuBody>
    </DropdownMenu>
  );
};
